<template>
  <div id="delivered">
    <div id="view-sub-nav" class="d-flex justify-space-between align-center">
      <DateRangePicker default="lastSixMonths" ref="dateRangePicker" @change="onDateRangeChange"></DateRangePicker>
      <div class="d-flex align-center flex-shrink-0">
        <v-chip size="small" color="primary"
          >Found {{ shipmentsWithGeo.length }} of {{ displayData.length }} Filtered Shipments</v-chip
        >
        <v-chip size="small" color="success" class="ml-2">Filtered Shipments Tally {{ grandTotalRevenue }}</v-chip>
        <v-switch
          class="ml-6"
          v-model="includeFocRecut"
          :label="includeFocRecut ? 'Including FOC/Recut' : 'Excluding FOC/Recut'"
          hide-details
          @update:model-value="onIncludeFocRecutChange"
        >
        </v-switch>
        <v-switch class="ml-6" v-model="showMap" :label="showMap ? 'Toggle to Chart' : 'Toggle to Map'" hide-details>
        </v-switch>
      </div>
    </div>
    <div v-if="showMap" class="map-frame">
      <div
        v-if="loading"
        class="d-flex justify-center align-center"
        style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          z-index: 1000;
        "
      >
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </div>
      <!-- DELIVERED LOCATION MAP -->
      <l-map id="map" :zoom="zoom" :center="center">
        <l-tile-layer :url="url"></l-tile-layer>
        <l-marker-cluster-group>
          <l-marker v-for="(s, index) in shipmentsWithGeo" :lat-lng="s.latlng" :key="index">
            <l-tooltip>
              <div>
                <span>Order: </span>
                <span>{{ s.orderNumber }}</span>
              </div>
              <div>
                <span>Customer: </span>
                <span>{{ s.customerEmail }}</span>
              </div>
              <div v-if="s.total">
                <span>Total: </span>
                <span>£{{ $numeral(s.total).format('0,0.00') }}</span>
              </div>
              <div>
                <span>Delivered: </span>
                <span>{{ $moment.unix(s.deliveryDate.seconds).format('Do MMM YYYY') }}</span>
              </div>
            </l-tooltip>
          </l-marker>
        </l-marker-cluster-group>
      </l-map>
    </div>
    <!-- DELIVERED LOCATION TABLE -->
    <div v-else>
      <div v-if="loading" class="d-flex justify-center align-center" style="height: 400px">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </div>
      <div v-else>
        <v-container fluid>
          <!-- One panel per region, in postcodeRegions order, OTHER last -->
          <v-expansion-panels v-for="region in regionSections" :key="region.section" class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <div class="d-flex justify-space-between align-center w-100">
                  <h3 class="ma-0 text-subtitle-1 font-weight-medium">{{ region.label }}</h3>
                  <div class="d-flex align-center">
                    <v-chip size="small" color="primary" class="mr-2">{{ region.orderCount }} Orders</v-chip>
                    <v-chip size="small" color="success" class="mr-2">{{ region.revenue }}</v-chip>
                    <v-chip variant="outlined" size="small" color="info" class="mr-4"
                      >{{ region.areas.length }} Areas</v-chip
                    >
                  </div>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-data-table
                  :headers="headers"
                  :items="region.areas"
                  class="elevation-1"
                  :items-per-page="50"
                  hide-default-footer
                ></v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-container>
      </div>
    </div>
  </div>
</template>

<script type="text/javascript">
import { LMap, LTileLayer, LMarker, LTooltip } from '@vue-leaflet/vue-leaflet'
import { Icon } from 'leaflet'
import DateRangePicker from '@/components/ui/DateRangePicker.vue'
import { comprehensivePostcodeMapping, postcodeRegions } from '@/js/postcodeMapping.js'
import { db } from '@/js/db'
import { collection, doc, documentId, getDocs, query, updateDoc, where } from 'firebase/firestore'

import LMarkerClusterGroup from '@/components/ui/LMarkerClusterGroup.vue'

delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

// Display names for the postcodeRegions keys, plus the synthetic OTHER bucket.
const SECTION_LABELS = {
  LONDON: 'London',
  SOUTH_EAST: 'South East England',
  SOUTH_WEST: 'South West England',
  EAST_OF_ENGLAND: 'East of England',
  MIDLANDS: 'Midlands',
  YORKSHIRE_HUMBER: 'Yorkshire and Humber',
  NORTH_WEST: 'North West England',
  NORTH_EAST: 'North East England',
  SCOTLAND: 'Scotland',
  WALES: 'Wales',
  NORTHERN_IRELAND: 'Northern Ireland',
  ISLE_OF_MAN: 'Isle of Man',
  OTHER: 'Other Areas'
}

export default {
  name: 'DeliveredLocation',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LMarkerClusterGroup,
    DateRangePicker
  },
  data() {
    return {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      zoom: 6,
      center: [54, -4.5],
      orders: [], // Filtered UK orders
      shipmentData: {}, // Map of orderID -> { latlng, postcode, shipmentId }
      showMap: true,
      loading: false,
      includeFocRecut: false,
      lastDateRange: null
    }
  },
  computed: {
    // Combine order data with shipment data for display
    displayData() {
      return this.orders.map((order) => {
        const shipment = this.shipmentData[order.id]
        return {
          orderID: order.id,
          orderNumber: order.orderNumber,
          customerEmail: order.customerEmail,
          deliveryDate: shipment?.deliveryDate || null,
          total: order.total,
          latlng: shipment?.latlng || null,
          shipmentId: shipment?.shipmentId || null,
          postcode: shipment?.postcode || null
        }
      })
    },
    // Filter displayData to only those with geo coordinates.
    shipmentsWithGeo() {
      return this.displayData.filter((item) => {
        return item.latlng && Array.isArray(item.latlng) && item.latlng.length === 2
      })
    },
    postcodeGroups() {
      const uniqueAreas = new Map()

      // Process entries and keep only unique area names (ignore duplicate postcode prefixes)
      Object.entries(comprehensivePostcodeMapping).forEach(([postcode, area]) => {
        if (!uniqueAreas.has(area)) {
          uniqueAreas.set(area, postcode)
        }
      })

      const allAreas = Array.from(uniqueAreas.entries()).map(([area, postcode]) => {
        // Determine section based on postcode prefix using postcodeRegions
        let section = 'OTHER'

        // Find which region this postcode prefix belongs to
        for (const [regionName, postcodes] of Object.entries(postcodeRegions)) {
          if (postcodes.includes(postcode)) {
            section = regionName
            break
          }
        }

        return { area, section, postcode }
      })

      // Initialize all areas with count 0 and total 0
      const groups = {}
      allAreas.forEach(({ area }) => {
        groups[area] = { count: 0, total: 0 }
      })

      // Count actual shipments using comprehensivePostcodeMapping
      let otherAreas = new Map() // Track unique unmatched postcode areas

      this.shipmentsWithGeo.forEach((shipment) => {
        if (shipment.postcode) {
          const postcode = shipment.postcode.toUpperCase().replace(/\s/g, '')
          let area = null

          // Extract postcode area letters (before first number) and find exact match
          const postcodeArea = postcode.match(/^[A-Z]+/)?.[0] || ''

          if (postcodeArea && comprehensivePostcodeMapping[postcodeArea]) {
            area = comprehensivePostcodeMapping[postcodeArea]
          }

          if (area && Object.prototype.hasOwnProperty.call(groups, area)) {
            groups[area].count++
            groups[area].total += shipment.total || 0
          } else {
            // Track unique unmatched postcode areas for OTHER section
            if (postcodeArea) {
              if (!otherAreas.has(postcodeArea)) {
                otherAreas.set(postcodeArea, postcodeArea)
              }
            }
          }
        }
      })

      // Add OTHER areas to the groups
      otherAreas.forEach((postcodeArea) => {
        groups[`OTHER_${postcodeArea}`] = { count: 0, total: 0 }
      })

      // Count orders for OTHER areas
      this.shipmentsWithGeo.forEach((shipment) => {
        if (shipment.postcode) {
          const postcode = shipment.postcode.toUpperCase().replace(/\s/g, '')
          const postcodeArea = postcode.match(/^[A-Z]+/)?.[0] || ''

          if (postcodeArea && !comprehensivePostcodeMapping[postcodeArea]) {
            groups[`OTHER_${postcodeArea}`].count++
            groups[`OTHER_${postcodeArea}`].total += shipment.total || 0
          }
        }
      })

      // Create OTHER areas array
      const otherAreasArray = Array.from(otherAreas.entries()).map(([postcodeArea, postcode]) => ({
        area: `OTHER_${postcodeArea}`,
        section: 'OTHER',
        postcode: postcode
      }))

      // Combine allAreas with OTHER areas
      const allAreasWithOther = [...allAreas, ...otherAreasArray]

      // Create result array with all areas in order
      return allAreasWithOther.map(({ area, section, postcode }) => ({
        postcodeArea: area,
        postcodePrefix: postcode,
        orderCount: groups[area].count,
        orderTotal: groups[area].total,
        orderTotalFormatted: `£${this.$numeral(groups[area].total).format('0,0.00')}`,
        section: section
      }))
    },
    regionSections() {
      return Object.keys(postcodeRegions)
        .concat('OTHER')
        .map((section) => {
          const areas = this.postcodeGroups.filter((area) => area.section === section)
          const revenueTotal = areas.reduce((sum, area) => sum + area.orderTotal, 0)
          return {
            section,
            label: SECTION_LABELS[section] || section,
            areas,
            orderCount: areas.reduce((sum, area) => sum + area.orderCount, 0),
            revenue: `£${this.$numeral(revenueTotal).format('0,0.00')}`
          }
        })
        .filter((region) => region.section !== 'OTHER' || region.areas.length > 0)
    },
    headers() {
      return [
        { title: 'Postcode Area', key: 'postcodeArea' },
        { title: 'Orders', key: 'orderCount', sortable: true },
        { title: 'Total', key: 'orderTotalFormatted', sortable: true }
      ]
    },
    // Grand total revenue across all regions
    grandTotalRevenue() {
      const total = this.displayData.reduce((sum, shipment) => sum + (shipment.total || 0), 0)
      return `£${this.$numeral(total).format('0,0.00')}`
    }
  },
  methods: {
    async fetchOrderData(orderIDs) {
      if (!orderIDs.length) return {}

      const data = {}
      const batches = []
      for (let i = 0; i < orderIDs.length; i += 10) {
        batches.push(orderIDs.slice(i, i + 10))
      }

      await Promise.all(
        batches.map(async (batch) => {
          const snapshot = await getDocs(query(collection(db, 'orders'), where(documentId(), 'in', batch)))
          snapshot.forEach((snap) => {
            const orderDoc = snap.data()
            data[snap.id] = {
              total: orderDoc.total || 0,
              type: orderDoc.type || null,
              region: orderDoc.region || null,
              orderNumber: orderDoc.orderNumber || null,
              customerEmail: orderDoc.customerEmail || null,
              deliveryCountry: orderDoc.customerContactDetails?.deliveryCountry || null,
              quoteID: orderDoc.quoteID || null,
              versionID: orderDoc.versionID || null,
              foc: false,
              recut: false
            }
          })
        })
      )

      return data
    },
    async fetchVersionFlags(quoteIDs) {
      const result = {}
      if (!quoteIDs.length) return result
      const batches = []
      for (let i = 0; i < quoteIDs.length; i += 10) {
        batches.push(quoteIDs.slice(i, i + 10))
      }
      await Promise.all(
        batches.map(async (batch) => {
          const snapshot = await getDocs(query(collection(db, 'quotes'), where(documentId(), 'in', batch)))
          snapshot.forEach((snap) => {
            result[snap.id] = snap.data().versions || []
          })
        })
      )
      return result
    },
    onDateRangeChange(range) {
      if (range && range.length === 2) {
        const dateRange = {
          start: range[0],
          end: range[1]
        }
        this.bindOrders(dateRange)
      }
    },
    onIncludeFocRecutChange() {
      if (this.lastDateRange) {
        this.bindOrders(this.lastDateRange)
      }
    },
    async bindOrders(dateRange) {
      if (!dateRange || !dateRange.start || !dateRange.end) {
        return
      }

      this.lastDateRange = dateRange
      this.loading = true

      try {
        const startTimestamp = this.$utils.tsFromDate(dateRange.start.toDate())
        const endTimestamp = this.$utils.tsFromDate(dateRange.end.toDate())

        // Step 1: Query shipments by deliveryDate range
        const shipmentsSnapshot = await getDocs(
          query(
            collection(db, 'shipments'),
            where('shipped', '==', true),
            where('deliveryDate', '>=', startTimestamp),
            where('deliveryDate', '<=', endTimestamp)
          )
        )

        const allShipments = shipmentsSnapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }))

        // Step 2: Get unique orderIDs and fetch orders
        const orderIDs = [...new Set(allShipments.filter((s) => s.orderID).map((s) => s.orderID))]
        const orderData = await this.fetchOrderData(orderIDs)

        // Resolve foc/recut from the quote version, not the order doc
        const quoteIDs = [
          ...new Set(
            Object.values(orderData)
              .map((o) => o.quoteID)
              .filter(Boolean)
          )
        ]
        const quoteVersions = await this.fetchVersionFlags(quoteIDs)
        Object.values(orderData).forEach((order) => {
          const versions = quoteVersions[order.quoteID] || []
          const version = versions.find((v) => v.id === order.versionID)
          order.foc = !!(version && version.foc)
          order.recut = !!(version && version.recut)
        })

        // Step 3: Filter orders by type/region/deliveryCountry (FOC/recut excluded unless toggle is on)
        const filteredOrderIDs = Object.entries(orderData)
          .filter(([, order]) => {
            if (order.type !== 'mega-admin') return false
            if (!this.includeFocRecut) {
              if (order.foc) return false
              if (order.recut) return false
            }
            if (order.region === 'uk') return true
            if (order.region === 'eu' && order.deliveryCountry === 'GB') return true
            return false
          })
          .map(([id]) => id)

        // Step 4: Filter shipments to only those matching filtered orders
        const filteredShipments = allShipments.filter((s) => filteredOrderIDs.includes(s.orderID))

        // Step 5: Build shipmentData map and orders array
        this.shipmentData = {}
        filteredShipments.forEach((s) => {
          this.shipmentData[s.orderID] = {
            shipmentId: s.id,
            latlng: s.latlng || null,
            postcode: s.postcode || null,
            deliveryDate: s.deliveryDate
          }
        })

        // Build orders array from filtered orderIDs
        this.orders = filteredOrderIDs.map((id) => ({
          id,
          ...orderData[id]
        }))

        // Step 6: Geocode items that need it
        const itemsNeedingGeo = this.displayData.filter((item) => item.postcode && !item.latlng)

        for (const item of itemsNeedingGeo) {
          await this.setLatLngFromPostcode(item)
        }

        // Force Vue reactivity by reassigning shipmentData
        this.shipmentData = { ...this.shipmentData }
      } catch (error) {
        console.error('Error loading UK orders:', error)
      } finally {
        this.loading = false
      }
    },
    async setLatLngFromPostcode(item) {
      try {
        if (!item.postcode) return
        if (item.latlng?.length === 2) return

        const cleanPostcode = item.postcode.replace(/[^A-Z0-9]/gi, '')
        const response = await fetch(`https://api.postcodes.io/postcodes/${cleanPostcode}`)
        const data = await response.json()

        if (data.result && data.result.latitude && data.result.longitude) {
          const lat = data.result.latitude
          const lng = data.result.longitude

          // Update shipment in database
          if (item.shipmentId) {
            try {
              await updateDoc(doc(db, 'shipments', item.shipmentId), {
                latlng: [lat, lng]
              })
            } catch (dbError) {
              console.error('Error updating shipment:', item.shipmentId, dbError)
            }
          }
          // Update local data
          item.latlng = [lat, lng]
          if (this.shipmentData[item.orderID]) {
            this.shipmentData[item.orderID].latlng = [lat, lng]
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 200))
      } catch (error) {
        console.error('Error geocoding:', item.postcode, error)
      }
    }
  },
  created() {
    this.$nextTick(() => {
      if (this.$refs.dateRangePicker) {
        this.$refs.dateRangePicker.setPreset(1)
        const twelveMonthRange = this.$refs.dateRangePicker.lastTwelveMonths()
        const dateRange = {
          start: twelveMonthRange[0],
          end: twelveMonthRange[1]
        }
        this.bindOrders(dateRange)
      }
    })
  }
}
</script>

<style lang="scss">
@import '~leaflet/dist/leaflet.css';
@import '~leaflet.markercluster/dist/MarkerCluster.css';
@import '~leaflet.markercluster/dist/MarkerCluster.Default.css';

#delivered {
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
}

#map {
  z-index: 0;
  height: 100%;
  width: 100%;
}

.map-frame {
  position: relative;
  width: 100%;
  height: $analytics-map-height;
}
</style>
